import { useEffect, useMemo, useRef } from 'react';
import { getSessionToken } from '../utils/api';

const HEARTBEAT_MS = 25000;
const MAX_RETRY_DELAY_MS = 30000;
const PROBE_TIMEOUT_MS = 4000;
const MAX_OUTBOX = 50;

// 채팅 WebSocket: 토큰으로 입장 → 이벤트 전달 → 끊기면 지수 백오프로 재연결
// onEvent 는 매 렌더의 최신 함수를 사용하므로 오래된 상태(stale closure)를 참조하지 않는다.
//
// 모바일에서 사진 선택 화면 등으로 앱이 백그라운드에 가 있으면 서버가 연결을 정리한다.
// 이때 브라우저는 끊긴 줄 모르고(OPEN 상태 유지) 보낸 메시지가 조용히 사라지므로,
//  - 연결이 준비되지 않았을 때 보낸 메시지는 보관했다가 입장 직후 순서대로 전송하고
//  - 앱으로 돌아오면 ping 으로 연결을 확인해, 응답이 없으면 즉시 새로 연결한다.
// 화면을 보고 있어도 와이파이 전환·공유기 유휴 정리·PC 절전 복귀 등으로 연결이 조용히 죽을 수 있다.
//  - 주기적인 ping 과 채팅 전송 직후 ping 에 pong 이 없으면 죽은 연결로 보고 다시 연결하고
//  - 서버 확인(같은 clientId 의 chat 응답)을 받지 못한 채팅은 새 연결에서 다시 보낸다 (서버가 clientId 로 중복 저장을 막음)
const ACKED_TYPES = new Set(['chat', 'send_failed', 'announcement_block']);
export function useChatSocket({ enabled, onEvent, onAuthFailure, onKicked }) {
    const wsRef = useRef(null);
    const readyRef = useRef(false);   // 입장했고, 살아 있는 연결로 확인됨
    const outboxRef = useRef([]);     // [{ clientId, payload }] 연결 준비 전에 보낸 것
    const inflightRef = useRef(new Map()); // clientId → payload: 보냈지만 서버 확인을 아직 못 받은 채팅
    const probeRef = useRef(() => {});
    const kickRef = useRef(() => {});
    const handlersRef = useRef({ onEvent, onAuthFailure, onKicked });
    handlersRef.current = { onEvent, onAuthFailure, onKicked };

    useEffect(() => {
        if (!enabled) return undefined;
        let retry = 0, retryTimer = null, heartbeat = null, probeTimer = null, disposed = false;
        const stopProbe = () => { clearTimeout(probeTimer); probeTimer = null; };

        const flush = () => {
            const ws = wsRef.current;
            while (readyRef.current && ws?.readyState === WebSocket.OPEN && outboxRef.current.length) {
                const { clientId, payload } = outboxRef.current.shift();
                if (clientId) inflightRef.current.set(clientId, payload);
                ws.send(payload);
            }
        };

        const connect = () => {
            clearTimeout(retryTimer);
            stopProbe();
            clearInterval(heartbeat);
            const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
            const ws = new WebSocket(`${proto}://${window.location.host}/ws`);
            wsRef.current = ws;
            readyRef.current = false;
            let authFailed = false;
            const isCurrent = () => wsRef.current === ws && !disposed;

            ws.onopen = () => {
                if (!isCurrent()) return;
                retry = 0;
                // 서버는 한 연결의 메시지를 받은 순서대로 처리하므로 join 바로 뒤에 보내도 안전하다
                ws.send(JSON.stringify({ type: 'join', token: getSessionToken() }));
                readyRef.current = true;
                // 이전 연결에서 확인받지 못한 채팅을 먼저(보낸 순서대로) 다시 보낸다
                for (const payload of inflightRef.current.values()) ws.send(payload);
                flush();
                heartbeat = setInterval(probe, HEARTBEAT_MS);
            };
            ws.onmessage = (ev) => {
                if (!isCurrent()) return;
                let data;
                try { data = JSON.parse(ev.data); } catch { return; }
                if (data.type === 'pong') {
                    stopProbe();
                    readyRef.current = true;
                    return flush();
                }
                if (data.clientId && ACKED_TYPES.has(data.type)) inflightRef.current.delete(data.clientId);
                if (data.type === 'error') authFailed = true;
                try { handlersRef.current.onEvent(data); }
                catch (err) { console.error('WebSocket 이벤트 처리 실패', data.type, err); }
            };
            ws.onclose = (event) => {
                if (!isCurrent()) return;
                clearInterval(heartbeat);
                stopProbe();
                readyRef.current = false;
                if (authFailed || event.code === 4001) return handlersRef.current.onAuthFailure?.();
                if (event.code === 1008) return handlersRef.current.onKicked?.(event.reason);
                const delay = Math.min(1000 * 2 ** retry, MAX_RETRY_DELAY_MS);
                retry += 1;
                retryTimer = setTimeout(connect, delay);
            };
        };

        // 열린 연결이 실제로 살아 있는지 ping 으로 확인, 응답이 없으면 새로 연결
        const probe = () => {
            if (disposed) return;
            const ws = wsRef.current;
            if (ws?.readyState !== WebSocket.OPEN || probeTimer) return;
            readyRef.current = false; // 확인될 때까지 새 메시지는 보관
            probeTimer = setTimeout(() => {
                probeTimer = null;
                if (wsRef.current !== ws || readyRef.current) return;
                retry = 0;
                connect();
                try { ws.close(); } catch {}
            }, PROBE_TIMEOUT_MS);
            try { ws.send('{"type":"ping"}'); } catch {}
        };
        // 연결 상태 확인: 끊겼으면 대기 없이 재연결, 열려 있으면 살아 있는지 확인
        const ensureConnection = () => {
            if (disposed) return;
            const ws = wsRef.current;
            if (!ws || ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
                retry = 0;
                return connect();
            }
            probe();
        };
        kickRef.current = ensureConnection;
        probeRef.current = probe;

        const onVisible = () => { if (document.visibilityState === 'visible') ensureConnection(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('online', ensureConnection);
        window.addEventListener('pageshow', ensureConnection);
        connect();

        return () => {
            disposed = true;
            stopProbe();
            kickRef.current = () => {};
            probeRef.current = () => {};
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('online', ensureConnection);
            window.removeEventListener('pageshow', ensureConnection);
            clearTimeout(retryTimer);
            clearInterval(heartbeat);
            wsRef.current?.close();
        };
    }, [enabled]);

    // send(data) 와 send.cancel(clientId): 사용자가 버린 채팅은 다시 보내지 않는다
    return useMemo(() => {
        const send = (data) => {
            const ws = wsRef.current;
            const payload = JSON.stringify(data);
            const clientId = data.type === 'chat' ? data.clientId : undefined;
            if (clientId) send.cancel(clientId);
            if (readyRef.current && ws?.readyState === WebSocket.OPEN) {
                ws.send(payload);
                if (!clientId) return;
                // 죽은 연결로 보냈다면 몇 초 안에 알아채고 새 연결에서 다시 보낸다
                inflightRef.current.set(clientId, payload);
                while (inflightRef.current.size > MAX_OUTBOX) inflightRef.current.delete(inflightRef.current.keys().next().value);
                return probeRef.current();
            }
            // 연결 준비 전: 보관했다가 입장 직후 전송 (타이핑 표시는 늦게 보내도 의미 없으므로 버림)
            if (data.type !== 'typing') {
                if (outboxRef.current.length >= MAX_OUTBOX) outboxRef.current.shift();
                outboxRef.current.push({ clientId, payload });
            }
            kickRef.current();
        };
        send.cancel = (clientId) => {
            inflightRef.current.delete(clientId);
            outboxRef.current = outboxRef.current.filter(item => item.clientId !== clientId);
        };
        return send;
    }, []);
}
